import React, { useEffect, useMemo, useRef, useState } from "react";

const REQUIRED_KEYS = [
  "Chatbot_API_URL",
  "buttonBackground",
  "debug",
  "sendButtonBackground",
  "sendButtonHoverBackground",
  "sendButtonIconColor",
  "sendButtonRadius",
  "closeButtonBackground",
  "closeButtonHoverBackground",
  "closeButtonIconColor",
  "closeButtonRadius",
  "recaptchaSiteKey",
  "model",
  "timeoutMs",
  "title",
];
const REQUIRED_KEY_SET = new Set(REQUIRED_KEYS);
const ATTACHMENT_SECTION_KEYS = [
  "attachmentButtonBackground",
  "attachmentButtonHoverBackground",
  "attachmentButtonIconColor",
  "attachmentButtonRadius",
  "attachmentButtonLogo",
];
const MIC_SECTION_KEYS = [
  "micButtonBackground",
  "micButtonHoverBackground",
  "micButtonIconColor",
  "micButtonRadius",
  "micButtonLogo",
];
const EXPAND_SECTION_KEYS = [
  "expandButtonBackground",
  "expandButtonHoverBackground",
  "expandButtonIconColor",
  "expandButtonRadius",
  "expandButtonLogo",
];
const BUBBLE_SECTION_KEYS = [
  "userBubbleColor",
  "userBubbleTextColor",
  "botBubbleColor",
  "botBubbleTextColor",
];
const WEBINAR_CONTEXT_SECTION_KEYS = [
  "display_text",
  "prompt",
  "webinarSummary",
  "webinarTranscript",
];

const buildDefaultConfig = () => ({
  Chatbot_API_URL: process.env.CHATBOT_API_URL,
  recaptchaSiteKey: process.env.RECAPTCHA_V3_SITE_KEY,
  model: process.env.CHATBOT_MODEL || "your_chatbot_model",
  buttonBackground:
    process.env.BUTTON_BACKGROUND ||
    "linear-gradient(135deg, oklch(0.55 0.22 264) 0%, oklch(0.42 0.22 264) 100%)",
  debug: true,

  timeoutMs: 30000,
  title: process.env.CHATBOT_TITLE || "Chatbot",

  greeting: "How can I help?",
  inputPlaceholder: "Type your message...",
  display_text: "",
  prompt: "",
  webinarSummary: "",
  webinarTranscript: "",
  customerName: "",
  customerLogo: "",
  primaryColor: "#2563eb",
  sendButtonBackground: "#2563eb",
  sendButtonHoverBackground: "#1d4ed8",
  sendButtonIconColor: "#ffffff",
  sendButtonRadius: 18,
  sendButtonLogo: "",
  attachmentButtonBackground: "transparent",
  attachmentButtonHoverBackground: "rgba(156, 163, 175, 0.12)",
  attachmentButtonIconColor: "#9ca3af",
  attachmentButtonRadius: 8,
  attachmentButtonLogo: "",
  micButtonBackground: "transparent",
  micButtonHoverBackground: "rgba(156, 163, 175, 0.12)",
  micButtonIconColor: "#9ca3af",
  micButtonRadius: 8,
  micButtonLogo: "",
  expandButtonBackground: "transparent",
  expandButtonHoverBackground: "#f3f4f6",
  expandButtonIconColor: "#6b7280",
  expandButtonRadius: 10,
  expandButtonLogo: "",
  closeButtonBackground: "transparent",
  closeButtonHoverBackground: "#f3f4f6",
  closeButtonIconColor: "#6b7280",
  closeButtonRadius: 10,
  closeButtonLogo: "",
  newBadge: false,
  triggerBadgeText: "New!",
  showAttachmentBtn: true,
  showMicBtn: true,
  showTriggerPill: true,
  triggerIcon: "sparkle",
  triggerLabel: "",
  triggerIconSize: 44,
  triggerIconRadius: 14,
  triggerIconBackground: "#2563eb",
  triggerIconColor: "#ffffff",
  headerVisible: true,
  expandButtonVisible: true,
  triggerPosition: "bottom-right",

  userBubbleColor: "#2563eb",
  userBubbleTextColor: "#ffffff",
  botBubbleColor: "#f3f4f6",
  botBubbleTextColor: "#111827",

  topics: [],
});

const isConfigObject = (value) =>
  !!value && typeof value === "object" && !Array.isArray(value);

const getAppLevelConfig = () => {
  if (typeof window === "undefined") return null;

  if (isConfigObject(window.__chatbotStudioConfig)) {
    return window.__chatbotStudioConfig;
  }

  if (isConfigObject(window.__embeddedChatbot?.config)) {
    return window.__embeddedChatbot.config;
  }

  return null;
};

const getInitialConfigSource = () => getAppLevelConfig() || buildDefaultConfig();

const ALLOWED_TOP_LEVEL_KEYS = Object.keys(getInitialConfigSource());
const ALLOWED_TOP_LEVEL_KEY_SET = new Set(ALLOWED_TOP_LEVEL_KEYS);
const ALLOWED_TOPIC_KEY_SET = new Set(["label", "icon", "prompt", "dropdown"]);
const ALLOWED_DROPDOWN_KEY_SET = new Set([
  "title",
  "description",
  "placeholder",
  "submitLabel",
  "options",
]);
const ALLOWED_DROPDOWN_OPTION_KEY_SET = new Set(["label", "value", "template"]);

const toPrettyJson = (value) => JSON.stringify(value, null, 2);
const cloneConfigValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value !== "object") return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

const parseJsonObject = (text) => {
  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { ok: false, error: "Config must be a JSON object." };
    }
    return { ok: true, value: parsed };
  } catch (error) {
    return { ok: false, error: `Invalid JSON: ${error.message}` };
  }
};

const isAdvancedValidationErrorMessage = (message) => {
  const text = String(message || "").trim();
  return (
    text.startsWith("Unknown key:") ||
    text.startsWith("Missing key:") ||
    text.startsWith("Missing required key:") ||
    text === "Config must be a JSON object." ||
    text.startsWith("Invalid JSON:") ||
    text === "timeoutMs must be a positive number." ||
    text === "debug must be a boolean." ||
    text === "topics must be an array." ||
    text.startsWith("topics[")
  );
};

const validateConfigObject = (config) => {
  const errors = [];

  Object.keys(config).forEach((key) => {
    if (!ALLOWED_TOP_LEVEL_KEY_SET.has(key)) {
      errors.push(`Unknown key: ${key}`);
    }
  });

  REQUIRED_KEYS.forEach((key) => {
    const value = config[key];
    if (value === undefined || value === null || value === "") {
      errors.push(`Missing required key: ${key}`);
    }
  });

  if (
    config.timeoutMs !== undefined &&
    (!Number.isFinite(Number(config.timeoutMs)) || Number(config.timeoutMs) <= 0)
  ) {
    errors.push("timeoutMs must be a positive number.");
  }

  if (config.debug !== undefined && typeof config.debug !== "boolean") {
    errors.push("debug must be a boolean.");
  }

  if (config.topics !== undefined && !Array.isArray(config.topics)) {
    errors.push("topics must be an array.");
  }

  if (Array.isArray(config.topics)) {
    config.topics.forEach((topic, index) => {
      if (!topic || typeof topic !== "object" || Array.isArray(topic)) {
        errors.push(`topics[${index}] must be an object.`);
        return;
      }

      Object.keys(topic).forEach((key) => {
        if (!ALLOWED_TOPIC_KEY_SET.has(key)) {
          errors.push(`Unknown key: topics[${index}].${key}`);
        }
      });

      if (!topic.label || typeof topic.label !== "string") {
        errors.push(`topics[${index}].label is required.`);
      }

      const hasDropdown =
        !!topic.dropdown &&
        typeof topic.dropdown === "object" &&
        !Array.isArray(topic.dropdown);

      if (topic.dropdown !== undefined && !hasDropdown) {
        errors.push(`topics[${index}].dropdown must be an object.`);
      }

      if (!hasDropdown) {
        if (!topic.prompt || typeof topic.prompt !== "string") {
          errors.push(`topics[${index}].prompt is required.`);
        }
        return;
      }

      Object.keys(topic.dropdown).forEach((key) => {
        if (!ALLOWED_DROPDOWN_KEY_SET.has(key)) {
          errors.push(`Unknown key: topics[${index}].dropdown.${key}`);
        }
      });

      const options = Array.isArray(topic.dropdown.options)
        ? topic.dropdown.options
        : [];
      if (options.length === 0) {
        errors.push(
          `topics[${index}].dropdown.options must include at least one option.`
        );
      }

      options.forEach((option, optionIndex) => {
        if (!option || typeof option !== "object" || Array.isArray(option)) {
          errors.push(
            `topics[${index}].dropdown.options[${optionIndex}] must be an object.`
          );
          return;
        }

        Object.keys(option).forEach((key) => {
          if (!ALLOWED_DROPDOWN_OPTION_KEY_SET.has(key)) {
            errors.push(
              `Unknown key: topics[${index}].dropdown.options[${optionIndex}].${key}`
            );
          }
        });

        const template =
          typeof option.template === "string"
            ? option.template.trim()
            : "";
        if (!template) {
          errors.push(
            `topics[${index}].dropdown.options[${optionIndex}].template is required when dropdown is enabled.`
          );
        }
      });
    });
  }

  return {
    ok: errors.length === 0,
    errors,
  };
};

const removeExistingChatbotDom = () => {
  document
    .querySelectorAll("#cb-container, #v0-chatbot-container")
    .forEach((el) => el.remove());
};

const asBoolean = (value) => Boolean(value);

const asTopics = (config) => (Array.isArray(config.topics) ? config.topics : []);

const getPickerColor = (value, fallback = "#2563eb") => {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(trimmed)) return trimmed;
  return fallback;
};

const getDropdownOptionLabel = (option) => {
  if (typeof option === "string") return option;
  if (!option || typeof option !== "object") return "";
  if (typeof option.label === "string") return option.label;
  if (typeof option.value === "string") return option.value;
  return "";
};

const getDropdownOptionTemplate = (option) => {
  if (!option || typeof option !== "object") return "";
  return typeof option.template === "string" ? option.template : "";
};

const toDropdownOptionObject = (option) => {
  if (typeof option === "string") {
    return {
      label: option,
      value: option,
      template: "",
    };
  }

  if (!option || typeof option !== "object") {
    return {
      label: "",
      value: "",
      template: "",
    };
  }

  const label = getDropdownOptionLabel(option);
  const value =
    typeof option.value === "string"
      ? option.value
      : label;
  const template = getDropdownOptionTemplate(option);

  return {
    ...option,
    label,
    value,
    template,
  };
};

const removeDropdownFallbackTemplate = (dropdown) => {
  if (!dropdown || typeof dropdown !== "object" || Array.isArray(dropdown)) {
    return dropdown;
  }

  const rest = { ...dropdown };
  delete rest.template;
  return rest;
};

const sanitizeConfigForStudio = (config) => {
  if (!config || typeof config !== "object" || Array.isArray(config)) {
    return config;
  }

  if (!Array.isArray(config.topics)) {
    return config;
  }

  return {
    ...config,
    topics: config.topics.map((topic) => {
      if (!topic || typeof topic !== "object" || Array.isArray(topic)) {
        return topic;
      }

      const hasDropdown =
        !!topic.dropdown &&
        typeof topic.dropdown === "object" &&
        !Array.isArray(topic.dropdown);
      if (!hasDropdown) {
        return topic;
      }

      const restTopic = { ...topic };
      delete restTopic.prompt;
      return {
        ...restTopic,
        dropdown: removeDropdownFallbackTemplate(topic.dropdown),
      };
    }),
  };
};

const normalizeConfigKeyId = (configKey) =>
  String(configKey || "config-key").replace(/[^a-zA-Z0-9_-]/g, "-");

function ConfigKeyField({
  configKey,
  label,
  enabled,
  onToggle,
  children,
  className = "form-label form-label-sm",
}) {
  const keyId = normalizeConfigKeyId(configKey);
  const checkboxId = `studio-key-toggle-${keyId}`;
  const fieldsetId = `studio-key-field-${keyId}`;
  const isRequiredKey = REQUIRED_KEY_SET.has(configKey);
  const isEnabled = isRequiredKey ? true : enabled;

  return (
    <>
      <div className="studio-key-label">
        {!isRequiredKey && (
          <input
            id={checkboxId}
            type="checkbox"
            className="form-check-input studio-key-toggle-input"
            checked={enabled}
            onChange={(e) => onToggle(configKey, e.target.checked)}
            aria-controls={fieldsetId}
          />
        )}
        <label
          htmlFor={!isRequiredKey ? checkboxId : undefined}
          className={`${className} mb-0`}
        >
          {label}
        </label>
      </div>
      <fieldset
        id={fieldsetId}
        disabled={!isEnabled}
        aria-disabled={!isEnabled}
        className={`studio-key-fieldset ${isEnabled ? "" : "is-disabled"}`}
      >
        {children}
      </fieldset>
    </>
  );
}

function ConfigKeyBooleanField({
  configKey,
  label,
  enabled,
  checked,
  onToggle,
  onChange,
  id,
}) {
  const keyId = normalizeConfigKeyId(configKey);
  const isRequiredKey = REQUIRED_KEY_SET.has(configKey);
  const isEnabled = isRequiredKey ? true : enabled;
  const presenceToggleId = `studio-key-toggle-${keyId}-presence`;
  const fieldsetId = `studio-key-boolean-field-${keyId}`;
  const valueToggleId = id || `studio-key-boolean-value-${keyId}`;

  return (
    <div className={`studio-key-boolean ${isEnabled ? "" : "is-disabled"}`}>
      <div className="studio-key-boolean-head">
        <label className="form-label form-label-sm fw-semibold mb-0" htmlFor={valueToggleId}>
          {label}
        </label>
        {!isRequiredKey && (
          <div className="form-check form-switch m-0 studio-key-boolean-presence">
            <input
              className="form-check-input"
              type="checkbox"
              id={presenceToggleId}
              checked={enabled}
              onChange={(e) => onToggle(configKey, e.target.checked)}
              aria-controls={fieldsetId}
            />
            <label className="form-check-label" htmlFor={presenceToggleId}>
              Include key
            </label>
          </div>
        )}
      </div>
      <fieldset
        id={fieldsetId}
        disabled={!isEnabled}
        aria-disabled={!isEnabled}
        className={`studio-key-fieldset ${isEnabled ? "" : "is-disabled"}`}
      >
        <div className="form-check form-switch m-0 studio-key-boolean-value">
          <input
            className="form-check-input"
            type="checkbox"
            id={valueToggleId}
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
          />
          <label className="form-check-label" htmlFor={valueToggleId}>
            {checked ? "On" : "Off"}
          </label>
        </div>
      </fieldset>
    </div>
  );
}

function ColorField({
  configKey,
  label,
  enabled,
  onToggle,
  value,
  onChange,
  fallback = "#2563eb",
}) {
  return (
    <div>
      <ConfigKeyField
        configKey={configKey}
        label={label}
        enabled={enabled}
        onToggle={onToggle}
      >
        <div className="d-flex align-items-center gap-2">
          <input
            type="color"
            className="form-control form-control-color p-1"
            style={{ width: "3rem", minWidth: "3rem" }}
            value={getPickerColor(value, fallback)}
            onChange={(e) => onChange(e.target.value)}
            title={label}
          />
          <input
            className="form-control form-control-sm"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      </ConfigKeyField>
    </div>
  );
}

export default function ChatbotStudio() {
  const defaultConfig = useMemo(() => {
    const initialSource = getInitialConfigSource();
    return sanitizeConfigForStudio(cloneConfigValue(initialSource));
  }, []);
  const [mode, setMode] = useState("simple");
  const [configObj, setConfigObj] = useState(defaultConfig);
  const [configInput, setConfigInput] = useState(toPrettyJson(defaultConfig));
  const [configInputError, setConfigInputError] = useState("");
  const [appliedConfigOutput, setAppliedConfigOutput] = useState(
    toPrettyJson(defaultConfig)
  );
  const [status, setStatus] = useState({
    type: "info",
    text: "Ready. Use Simple Builder or Advanced JSON and click Run Chatbot.",
    items: [],
  });
  const [isRunning, setIsRunning] = useState(false);
  const [keyEnabledMap, setKeyEnabledMap] = useState({});
  const fileRef = useRef(null);
  const transcriptFileRef = useRef(null);

  useEffect(() => {
    setKeyEnabledMap((prev) => {
      if (!configObj || typeof configObj !== "object" || Array.isArray(configObj)) {
        return prev;
      }

      const next = { ...prev };
      let changed = false;

      ALLOWED_TOP_LEVEL_KEYS.forEach((key) => {
        const shouldEnable = REQUIRED_KEY_SET.has(key)
          ? true
          : Object.prototype.hasOwnProperty.call(configObj, key);
        if (next[key] !== shouldEnable) {
          next[key] = shouldEnable;
          changed = true;
        }
      });

      const shouldEnableBubbleSection = BUBBLE_SECTION_KEYS.some((key) =>
        Object.prototype.hasOwnProperty.call(configObj, key)
      );
      if (next.__bubbleSection !== shouldEnableBubbleSection) {
        next.__bubbleSection = shouldEnableBubbleSection;
        changed = true;
      }

      const shouldEnableWebinarContextSection = WEBINAR_CONTEXT_SECTION_KEYS.some((key) =>
        Object.prototype.hasOwnProperty.call(configObj, key)
      );
      if (next.__webinarContextSection !== shouldEnableWebinarContextSection) {
        next.__webinarContextSection = shouldEnableWebinarContextSection;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [configObj]);

  useEffect(() => {
    const handlePayloadConfigUpdated = (event) => {
      const payload = event?.detail;
      if (!payload || typeof payload !== "object") return;

      const patch = {};
      if (typeof payload.display_text === "string") {
        patch.display_text = payload.display_text;
      }
      if (typeof payload.prompt === "string") {
        patch.prompt = payload.prompt;
      }
      if (typeof payload.webinar_summary === "string") {
        patch.webinarSummary = payload.webinar_summary;
      }
      if (typeof payload.webinar_transcript === "string") {
        patch.webinarTranscript = payload.webinar_transcript;
      }

      const keys = Object.keys(patch);
      if (keys.length === 0) return;

      setKeyEnabledMap((prev) => ({
        ...prev,
        __webinarContextSection: true,
        display_text: true,
        prompt: true,
        webinarSummary: true,
        webinarTranscript: true,
      }));

      setConfigObj((prev) => {
        const next = sanitizeConfigForStudio({
          ...prev,
          ...patch,
        });
        setConfigInput(toPrettyJson(next));
        setConfigInputError("");
        return next;
      });

      setStatus({
        type: "info",
        text: "Payload Config changes synced from chatbot preview.",
        items: [],
      });
    };

    window.addEventListener("chatbot:payloadConfigUpdated", handlePayloadConfigUpdated);
    return () => {
      window.removeEventListener("chatbot:payloadConfigUpdated", handlePayloadConfigUpdated);
    };
  }, []);

  useEffect(() => {
    const updateSummarizeConfig = (patch) => {
      if (!patch || typeof patch !== "object") return;

      const nextPatch = {};
      if (typeof patch.display_text === "string") {
        nextPatch.display_text = patch.display_text;
      }
      if (typeof patch.prompt === "string") {
        nextPatch.prompt = patch.prompt;
      }
      if (typeof patch.webinarSummary === "string") {
        nextPatch.webinarSummary = patch.webinarSummary;
      }
      if (typeof patch.webinarTranscript === "string") {
        nextPatch.webinarTranscript = patch.webinarTranscript;
      }

      if (Object.keys(nextPatch).length === 0) return;

      setKeyEnabledMap((prev) => ({
        ...prev,
        __webinarContextSection: true,
        display_text: true,
        prompt: true,
        webinarSummary: true,
        webinarTranscript: true,
      }));

      setConfigObj((prev) => {
        const next = sanitizeConfigForStudio({
          ...prev,
          ...nextPatch,
        });
        setConfigInput(toPrettyJson(next));
        setConfigInputError("");
        return next;
      });
    };

    window.updateSummarizeButton = (label) => {
      if (typeof label !== "string") return;
      updateSummarizeConfig({ display_text: label });
    };

    window.updateSummarizePrompt = (prompt) => {
      if (typeof prompt !== "string") return;
      updateSummarizeConfig({ prompt });
    };

    window.updateWebinarSummary = (webinarSummary) => {
      if (typeof webinarSummary !== "string") return;
      updateSummarizeConfig({ webinarSummary });
    };

    window.updateWebinarTranscript = (webinarTranscript) => {
      if (typeof webinarTranscript !== "string") return;
      updateSummarizeConfig({ webinarTranscript });
    };

    window.updateSummarizePayload = (patch) => {
      updateSummarizeConfig(patch);
    };

    return () => {
      delete window.updateSummarizeButton;
      delete window.updateSummarizePrompt;
      delete window.updateWebinarSummary;
      delete window.updateWebinarTranscript;
      delete window.updateSummarizePayload;
    };
  }, []);

  const isKeyEnabled = (configKey) => {
    if (REQUIRED_KEY_SET.has(configKey)) return true;
    return keyEnabledMap[configKey] !== false;
  };

  const toggleKeyEnabled = (configKey, isEnabled) => {
    if (REQUIRED_KEY_SET.has(configKey)) return;

    setKeyEnabledMap((prev) => ({
      ...prev,
      [configKey]: Boolean(isEnabled),
    }));

    updateConfigFromForm((prev) => {
      const hasKey = Object.prototype.hasOwnProperty.call(prev, configKey);
      if (isEnabled) {
        if (hasKey) return prev;
        if (Object.prototype.hasOwnProperty.call(defaultConfig, configKey)) {
          return { ...prev, [configKey]: cloneConfigValue(defaultConfig[configKey]) };
        }
        return { ...prev, [configKey]: "" };
      }

      if (!hasKey) return prev;
      const next = { ...prev };
      delete next[configKey];
      return next;
    });
  };

  const setErrorStatus = (errors) => {
    const items = Array.isArray(errors) ? errors.map((item) => String(item)) : [String(errors)];
    const hasMultiple = items.length > 1;
    setStatus({
      type: "danger",
      text: hasMultiple ? "Validation failed. Fix the following issues:" : items[0],
      items,
    });
  };

  const statusErrorItems = useMemo(
    () => (status.type === "danger" && Array.isArray(status.items) ? status.items : []),
    [status]
  );

  const advancedErrorItems = useMemo(() => {
    const merged = [];
    if (configInputError) merged.push(configInputError);
    statusErrorItems
      .filter((item) => isAdvancedValidationErrorMessage(item))
      .forEach((item) => {
        if (!merged.includes(item)) merged.push(item);
      });
    return merged;
  }, [configInputError, statusErrorItems]);

  const advancedHasErrors = advancedErrorItems.length > 0;

  const syncConfigEverywhere = (nextConfig) => {
    const sanitized = sanitizeConfigForStudio(nextConfig);
    setConfigObj(sanitized);
    setConfigInput(toPrettyJson(sanitized));
    setConfigInputError("");
  };

  const updateConfigFromForm = (updater) => {
    setConfigObj((prev) => {
      const next = sanitizeConfigForStudio(updater(prev));
      setConfigInput(toPrettyJson(next));
      setConfigInputError("");
      return next;
    });
  };

  const handleJsonInputChange = (value) => {
    setConfigInput(value);

    const parsed = parseJsonObject(value);
    if (!parsed.ok) {
      setConfigInputError(parsed.error);
      return;
    }

    setConfigInputError("");
    setConfigObj(sanitizeConfigForStudio(parsed.value));
  };

  const parseAndValidateCurrentInput = () => {
    const parsed = parseJsonObject(configInput);
    if (!parsed.ok) {
      setConfigInputError(parsed.error);
      return { ok: false, errors: [parsed.error] };
    }

    const sanitized = sanitizeConfigForStudio(parsed.value);
    const validation = validateConfigObject(sanitized);
    if (!validation.ok) {
      return { ok: false, errors: validation.errors };
    }

    return { ok: true, value: sanitized };
  };

  const handleValidate = () => {
    const result = parseAndValidateCurrentInput();
    if (!result.ok) {
      setErrorStatus(result.errors);
      return;
    }

    syncConfigEverywhere(result.value);
    setStatus({
      type: "success",
      text: "Config is valid and synced between Simple and Advanced modes.",
    });
  };

  const handleFormatJson = () => {
    const parsed = parseJsonObject(configInput);
    if (!parsed.ok) {
      setConfigInputError(parsed.error);
      setErrorStatus(parsed.error);
      return;
    }

    syncConfigEverywhere(parsed.value);
    setStatus({
      type: "success",
      text: "JSON formatted.",
    });
  };

  const handleReset = () => {
    syncConfigEverywhere(defaultConfig);
    setAppliedConfigOutput(toPrettyJson(defaultConfig));
    setStatus({
      type: "info",
      text: "Reset to default chatbot configuration.",
    });
  };

  const handleCopyOutput = async () => {
    try {
      await navigator.clipboard.writeText(appliedConfigOutput);
      setStatus({
        type: "success",
        text: "Applied output JSON copied to clipboard.",
      });
    } catch (error) {
      setErrorStatus("Unable to copy JSON to clipboard.");
    }
  };

  const handleCopyAdvancedJson = async () => {
    try {
      await navigator.clipboard.writeText(configInput);
      setStatus({
        type: "success",
        text: "Advanced JSON copied to clipboard.",
        items: [],
      });
    } catch (error) {
      setErrorStatus("Unable to copy Advanced JSON to clipboard.");
    }
  };

  const handleExport = () => {
    const blob = new Blob([appliedConfigOutput], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "chatbot-config.json";
    anchor.click();
    URL.revokeObjectURL(url);

    setStatus({
      type: "success",
      text: "Applied config exported.",
    });
  };

  const handleImportClick = () => {
    if (fileRef.current) fileRef.current.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;

    const text = await file.text();
    const parsed = parseJsonObject(text);
    if (!parsed.ok) {
      setConfigInputError(parsed.error);
      setErrorStatus(parsed.error);
      event.target.value = "";
      return;
    }

    syncConfigEverywhere(parsed.value);
    setStatus({
      type: "success",
      text: "Config imported and synced.",
    });

    event.target.value = "";
  };

  const handleTranscriptUploadClick = () => {
    if (transcriptFileRef.current) transcriptFileRef.current.click();
  };

  const handleTranscriptFileUpload = async (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      setFieldAlways("webinarTranscript", text);
      setStatus({
        type: "success",
        text: `Transcript loaded from ${file.name}.`,
      });
    } catch (error) {
      setErrorStatus(`Unable to read transcript file: ${error.message}`);
    } finally {
      event.target.value = "";
    }
  };

  const handleRunChatbot = async () => {
    const result = parseAndValidateCurrentInput();
    if (!result.ok) {
      setErrorStatus(result.errors);
      return false;
    }

    setIsRunning(true);
    try {
      syncConfigEverywhere(result.value);
      removeExistingChatbotDom();
      const { initChatbot } = await import("@/chatbot_embed/index.js");
      initChatbot(result.value);

      const pretty = toPrettyJson(result.value);
      setAppliedConfigOutput(pretty);
      setStatus({
        type: "success",
        text: "Chatbot launched with the current configuration.",
      });
      return true;
    } catch (error) {
      setErrorStatus(`Failed to initialize chatbot: ${error.message}`);
      return false;
    } finally {
      setIsRunning(false);
    }
  };

  const setField = (key, value) => {
    if (!isKeyEnabled(key)) return;
    updateConfigFromForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const setFieldAlways = (key, value) => {
    setKeyEnabledMap((prev) => ({
      ...prev,
      [key]: true,
    }));
    updateConfigFromForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const setNumberField = (key, value) => {
    if (!isKeyEnabled(key)) return;
    const numeric = Number(value);
    setField(key, Number.isFinite(numeric) ? numeric : value);
  };

  const setSectionEnabled = (toggleKey, sectionKeys, enabled) => {
    if (enabled) {
      toggleKeyEnabled(toggleKey, true);
      setFieldAlways(toggleKey, true);
      sectionKeys.forEach((key) => toggleKeyEnabled(key, true));
      return;
    }

    setFieldAlways(toggleKey, false);
    sectionKeys.forEach((key) => toggleKeyEnabled(key, false));
    toggleKeyEnabled(toggleKey, false);
  };

  const setKeyGroupEnabled = (sectionStateKey, sectionKeys, enabled) => {
    const targetState = Boolean(enabled);
    setKeyEnabledMap((prev) => ({
      ...prev,
      [sectionStateKey]: targetState,
    }));
    sectionKeys.forEach((key) => toggleKeyEnabled(key, targetState));
  };

  const topics = asTopics(configObj);
  const attachmentSectionEnabled = asBoolean(configObj.showAttachmentBtn);
  const micSectionEnabled = asBoolean(configObj.showMicBtn);
  const expandSectionEnabled = asBoolean(configObj.expandButtonVisible);
  const bubbleSectionEnabled = keyEnabledMap.__bubbleSection !== false;
  const webinarContextSectionEnabled = keyEnabledMap.__webinarContextSection !== false;

  const addTopic = () => {
    updateConfigFromForm((prev) => ({
      ...prev,
      topics: [
        ...asTopics(prev),
        {
          label: "New topic",
          icon: "key",
          prompt: "Ask a question",
        },
      ],
    }));
  };

  const removeTopic = (index) => {
    updateConfigFromForm((prev) => ({
      ...prev,
      topics: asTopics(prev).filter((_, i) => i !== index),
    }));
  };

  const updateTopic = (index, key, value) => {
    updateConfigFromForm((prev) => ({
      ...prev,
      topics: asTopics(prev).map((topic, i) =>
        i === index ? { ...topic, [key]: value } : topic
      ),
    }));
  };

  const toggleTopicDropdown = (index, enabled) => {
    updateConfigFromForm((prev) => ({
      ...prev,
      topics: asTopics(prev).map((topic, i) => {
        if (i !== index) return topic;
        if (!enabled) {
          const rest = { ...topic };
          delete rest.dropdown;
          return {
            ...rest,
            prompt:
              typeof rest.prompt === "string" && rest.prompt.trim()
                ? rest.prompt
                : "Ask a question",
          };
        }
        const cleanedDropdown = removeDropdownFallbackTemplate(topic.dropdown || {});
        const existingOptions = Array.isArray(cleanedDropdown.options)
          ? cleanedDropdown.options
          : [];
        const topicWithoutPrompt = { ...topic };
        delete topicWithoutPrompt.prompt;
        return {
          ...topicWithoutPrompt,
          dropdown: {
            ...cleanedDropdown,
            title: cleanedDropdown.title || "Choose an option",
            description: cleanedDropdown.description || "",
            placeholder: cleanedDropdown.placeholder || "Select an option",
            submitLabel: cleanedDropdown.submitLabel || "Ask AI",
            options:
              existingOptions.length > 0
                ? existingOptions
                : [{ label: "Option 1", value: "Option 1", template: "" }],
          },
        };
      }),
    }));
  };

  const updateTopicDropdown = (index, key, value) => {
    updateConfigFromForm((prev) => ({
      ...prev,
      topics: asTopics(prev).map((topic, i) => {
        if (i !== index || !topic.dropdown) return topic;
        return {
          ...topic,
          dropdown: {
            ...topic.dropdown,
            [key]: value,
          },
        };
      }),
    }));
  };

  const addDropdownOption = (index) => {
    updateConfigFromForm((prev) => ({
      ...prev,
      topics: asTopics(prev).map((topic, i) => {
        if (i !== index || !topic.dropdown) return topic;
        const current = Array.isArray(topic.dropdown.options)
          ? topic.dropdown.options
          : [];
        return {
          ...topic,
          dropdown: {
            ...topic.dropdown,
            options: [
              ...current,
              { label: "New option", value: "New option", template: "" },
            ],
          },
        };
      }),
    }));
  };

  const updateDropdownOption = (topicIndex, optionIndex, key, value) => {
    updateConfigFromForm((prev) => ({
      ...prev,
      topics: asTopics(prev).map((topic, i) => {
        if (i !== topicIndex || !topic.dropdown) return topic;
        const current = Array.isArray(topic.dropdown.options)
          ? topic.dropdown.options
          : [];
        return {
          ...topic,
          dropdown: {
            ...topic.dropdown,
            options: current.map((option, idx) => {
              if (idx !== optionIndex) return option;

              const prevLabel = getDropdownOptionLabel(option);
              const next = {
                ...toDropdownOptionObject(option),
                [key]: value,
              };

              if (key === "label") {
                const hadCustomValue =
                  typeof option === "object" &&
                  option !== null &&
                  typeof option.value === "string" &&
                  option.value !== prevLabel;

                if (!hadCustomValue) {
                  next.value = value;
                }
              }

              return next;
            }),
          },
        };
      }),
    }));
  };

  const removeDropdownOption = (topicIndex, optionIndex) => {
    updateConfigFromForm((prev) => ({
      ...prev,
      topics: asTopics(prev).map((topic, i) => {
        if (i !== topicIndex || !topic.dropdown) return topic;
        const current = Array.isArray(topic.dropdown.options)
          ? topic.dropdown.options
          : [];
        return {
          ...topic,
          dropdown: {
            ...topic.dropdown,
            options: current.filter((_, idx) => idx !== optionIndex),
          },
        };
      }),
    }));
  };

  const simpleCol = mode === "simple" ? "col-12 col-xl-7" : "col-12 col-xl-5";
  const advancedCol = mode === "simple" ? "col-12 col-xl-5" : "col-12 col-xl-7";


  const SummarizeButton = ({ disabled = false }) => {
    const summarizeDisplayText =
      typeof configObj.display_text === "string" && configObj.display_text.trim()
        ? configObj.display_text.trim()
        : "Summarize Webinar";
    const summarizePrompt =
      typeof configObj.prompt === "string" && configObj.prompt.trim()
        ? configObj.prompt.trim()
        : "Summarize the webinar video for this investment offering. Provide key takeaways and important points discussed.";
    const webinarSummary = configObj.webinarSummary;
    const webinarTranscript =  configObj.webinarTranscript;

    const handleClick = async (e) => {
      e.preventDefault();
      if (disabled) return;

      const anchor = e.currentTarget;
      const runtimeDisplayText =
        typeof anchor?.dataset?.displayText === "string" && anchor.dataset.displayText.trim()
          ? anchor.dataset.displayText.trim()
          : summarizeDisplayText;
      const runtimePrompt =
        typeof anchor?.dataset?.prompt === "string" && anchor.dataset.prompt.trim()
          ? anchor.dataset.prompt.trim()
          : summarizePrompt;
      const runtimeWebinarSummary =
        typeof anchor?.dataset?.webinarSummary === "string"
          ? anchor.dataset.webinarSummary
          : webinarSummary;
      const runtimeWebinarTranscript =
        typeof anchor?.dataset?.webinarTranscript === "string"
          ? anchor.dataset.webinarTranscript
          : webinarTranscript;

      let sendPromptFn = window.__sendPromptToChatbot;
      let chatbot = window.__embeddedChatbot;
      if (typeof sendPromptFn !== "function" || !chatbot) {
        const launched = await handleRunChatbot();
        if (!launched) return;
        sendPromptFn = window.__sendPromptToChatbot;
        chatbot = window.__embeddedChatbot;
      }

      if (typeof sendPromptFn === "function") {
        const win = document.getElementById("cb-window");
        if (chatbot && win && !win.classList.contains("open")) {
          chatbot.toggleChat();
        }

        await sendPromptFn({
          prompt: runtimePrompt,
          displayText: runtimeDisplayText,
          webinarSummary: runtimeWebinarSummary,
          webinarTranscript: runtimeWebinarTranscript,
        });
      }
    };

    return (
      <a
        href="#"
        className={`summarize-webinar-btn${disabled ? " is-disabled" : ""}`}
        style={{ display: "inline-flex", pointerEvents: disabled ? "none" : "auto" }}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        data-cms-ai="0"
        data-display-text={summarizeDisplayText}
        data-prompt={summarizePrompt}
        data-webinar-summary={webinarSummary}
        data-webinar-transcript={webinarTranscript}
      >
        {summarizeDisplayText}
      </a>
    );
  };

  return (
    <div className="chatbot-studio-page container-fluid py-4 ps-4 pe-3 pe-lg-4">
      <style>{`
        .chatbot-studio-page {
          --studio-ink: #16253f;
          --studio-muted: #506487;
          --studio-line: #d8e4f8;
          --studio-panel: #ffffff;
          --studio-accent: #2f6ff4;
          position: relative;
          font-family: "Sora", "Manrope", "Avenir Next", "Segoe UI", sans-serif;
          background:
            radial-gradient(circle at 12% 18%, rgba(80, 139, 255, 0.2) 0, rgba(80, 139, 255, 0) 42%),
            radial-gradient(circle at 90% 10%, rgba(98, 221, 201, 0.17) 0, rgba(98, 221, 201, 0) 38%),
            linear-gradient(180deg, #f4f8ff 0%, #eef4ff 55%, #f7f9fc 100%);
          border-radius: 22px;
          border: 1px solid #e3ebf9;
          overflow: hidden;
        }
        .chatbot-studio-page .studio-shell {
          position: relative;
          z-index: 1;
          max-width: 1640px;
          margin: 0 auto;
        }
        .chatbot-studio-page .studio-glow {
          position: absolute;
          width: 360px;
          height: 360px;
          border-radius: 50%;
          pointer-events: none;
          filter: blur(68px);
          opacity: 0.45;
          z-index: 0;
        }
        .chatbot-studio-page .studio-glow-1 {
          top: -145px;
          right: -70px;
          background: #7ca2ff;
        }
        .chatbot-studio-page .studio-glow-2 {
          bottom: -190px;
          left: -90px;
          background: #76d4c8;
        }
        .chatbot-studio-page .studio-hero {
          position: relative;
          overflow: hidden;
          background: linear-gradient(128deg, #0f2245 0%, #1c4086 45%, #2e62bf 100%);
          color: #eff4ff;
          border-radius: 18px;
          padding: 1.15rem 1.2rem;
          box-shadow: 0 16px 36px rgba(13, 33, 74, 0.28);
          border: 1px solid rgba(255, 255, 255, 0.16);
        }
        .chatbot-studio-page .studio-hero::after {
          content: "";
          position: absolute;
          width: 280px;
          height: 280px;
          right: -100px;
          top: -140px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.24) 0%, rgba(255, 255, 255, 0) 72%);
        }
        .chatbot-studio-page .studio-kicker {
          display: inline-flex;
          align-items: center;
          padding: 0.26rem 0.72rem;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.18);
          border: 1px solid rgba(255, 255, 255, 0.33);
          font-size: 0.72rem;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          font-weight: 700;
          margin-bottom: 0.45rem;
        }
        .chatbot-studio-page .studio-hero h4 {
          font-weight: 700;
          letter-spacing: 0.01em;
        }
        .chatbot-studio-page .studio-hero p {
          color: rgba(238, 244, 255, 0.92);
          max-width: 860px;
        }
        .chatbot-studio-page .studio-hero-meta {
          position: relative;
          z-index: 1;
          display: flex;
          flex-wrap: wrap;
          gap: 0.45rem;
          margin-top: 0.85rem;
        }
        .chatbot-studio-page .studio-meta-pill {
          border-radius: 999px;
          font-size: 0.74rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          color: rgba(236, 245, 255, 0.95);
          background: rgba(255, 255, 255, 0.14);
          border: 1px solid rgba(255, 255, 255, 0.24);
          padding: 0.3rem 0.62rem;
        }
        .chatbot-studio-page .studio-alert {
          border-radius: 13px;
          border: none;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.09);
          font-weight: 500;
        }
        .chatbot-studio-page .studio-toolbar {
          position: sticky;
          top: 10px;
          z-index: 5;
          background: rgba(255, 255, 255, 0.88);
          border: 1px solid #d7e3f7;
          border-radius: 15px;
          padding: 0.58rem;
          backdrop-filter: blur(10px);
          box-shadow: 0 10px 24px rgba(24, 43, 83, 0.08);
        }
        .chatbot-studio-page .studio-mode-group {
          display: flex;
          gap: 0.5rem;
          margin-right: 0.35rem;
        }
        .chatbot-studio-page .studio-toolbar .btn {
          border-radius: 999px;
          font-weight: 600;
          letter-spacing: 0.01em;
          border-width: 1px;
          font-size: 0.82rem;
          padding: 0.5rem 0.68rem;
          line-height: 1.2;
          box-shadow: 0 1px 0 rgba(255, 255, 255, 0.32) inset;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .chatbot-studio-page .studio-toolbar .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 12px rgba(20, 39, 77, 0.12);
        }
        .chatbot-studio-page .studio-toolbar .studio-run-btn {
          background: #0f766e;
          border-color: #0f766e;
          color: #ffffff;
        }
        .chatbot-studio-page .studio-toolbar .studio-run-btn:hover,
        .chatbot-studio-page .studio-toolbar .studio-run-btn:focus {
          background: #0b5f59;
          border-color: #0b5f59;
          color: #ffffff;
        }
        .chatbot-studio-page .studio-toolbar .studio-run-btn:disabled {
          background: #7aa8a3;
          border-color: #7aa8a3;
          color: #e9f3f2;
        }
        .chatbot-studio-page .studio-card {
          border-radius: 18px;
          border: 1px solid #d4e1f6;
          overflow: hidden;
          box-shadow: 0 20px 38px rgba(15, 23, 42, 0.1);
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.97) 0%, rgba(251, 253, 255, 0.95) 100%);
        }
        .chatbot-studio-page .studio-card-header {
          background: linear-gradient(180deg, #f5f8ff 0%, #edf3ff 100%);
          color: #1f3258;
          border-bottom: 1px solid #d6e2f5;
          font-size: 0.95rem;
          letter-spacing: 0.015em;
          font-weight: 700;
        }
        .chatbot-studio-page .studio-scroll-body {
          max-height: 70vh;
          overflow-y: auto;
          padding: 1.05rem;
        }
        .chatbot-studio-page .studio-scroll-body::-webkit-scrollbar {
          width: 8px;
        }
        .chatbot-studio-page .studio-scroll-body::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #c2d4f6 0%, #9cb8ee 100%);
          border-radius: 999px;
        }
        .chatbot-studio-page .studio-section {
          border: 1px solid #dbe6f8;
          border-radius: 14px;
          background: linear-gradient(180deg, #fafdff 0%, #f5f9ff 100%);
          padding: 0.95rem;
          margin-bottom: 0.95rem;
          box-shadow: 0 8px 20px rgba(22, 39, 74, 0.05);
        }
        .chatbot-studio-page .studio-section h6 {
          color: #182d53;
          font-weight: 700;
          letter-spacing: 0.015em;
        }
        .chatbot-studio-page .studio-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.65rem;
          margin-bottom: 0.5rem;
        }
        .chatbot-studio-page .summarize-webinar-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          text-decoration: none;
          border-radius: 999px;
          border: 1px solid #b8cdf4;
          background: linear-gradient(180deg, #f2f7ff 0%, #e9f1ff 100%);
          color: #1e4d98;
          font-size: 0.76rem;
          font-weight: 700;
          padding: 0.32rem 0.62rem;
          white-space: nowrap;
        }
        .chatbot-studio-page .summarize-webinar-btn:hover {
          background: linear-gradient(180deg, #eaf2ff 0%, #deebff 100%);
          border-color: #9fbbef;
          color: #133f82;
        }
        .chatbot-studio-page .summarize-webinar-btn.is-disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
        .chatbot-studio-page .summarize-webinar-btn svg {
          width: 14px;
          height: 14px;
        }
        .chatbot-studio-page .studio-code-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
        }
        .chatbot-studio-page .studio-code-copy-btn {
          border-radius: 999px;
          font-size: 0.74rem;
          font-weight: 700;
          padding: 0.24rem 0.58rem;
          border: 1px solid #9fbbef;
          background: rgba(255, 255, 255, 0.92);
          color: #264f95;
          line-height: 1.15;
        }
        .chatbot-studio-page .studio-code-copy-btn:hover {
          background: #ffffff;
          border-color: #7ea2e4;
          color: #173d7f;
        }
        .chatbot-studio-page .studio-section .form-label-sm {
          color: #30476f;
          font-weight: 600;
        }
        .chatbot-studio-page .studio-key-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.35rem;
        }
        .chatbot-studio-page .studio-key-toggle-input {
          margin-top: 0;
        }
        .chatbot-studio-page .studio-key-fieldset {
          margin: 0;
          padding: 0;
          border: 0;
          min-width: 0;
        }
        .chatbot-studio-page .studio-key-fieldset.is-disabled {
          opacity: 0.58;
        }
        .chatbot-studio-page .studio-subsection-fieldset {
          border: 0;
          margin: 0;
          padding: 0;
          min-width: 0;
        }
        .chatbot-studio-page .studio-subsection-fieldset.is-disabled {
          opacity: 0.58;
        }
        .chatbot-studio-page .form-control-sm,
        .chatbot-studio-page .form-select-sm,
        .chatbot-studio-page .form-control {
          border-color: #d1def2;
          border-radius: 10px;
          background-color: rgba(255, 255, 255, 0.88);
        }
        .chatbot-studio-page .form-control-sm:focus,
        .chatbot-studio-page .form-select-sm:focus,
        .chatbot-studio-page .form-control:focus {
          border-color: #7ea2ef;
          box-shadow: 0 0 0 0.2rem rgba(74, 120, 218, 0.15);
        }
        .chatbot-studio-page .studio-toggles {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
          gap: 0.55rem;
        }
        .chatbot-studio-page .studio-key-boolean {
          border: 1px solid #d5e1f5;
          border-radius: 12px;
          background: linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%);
          box-shadow: 0 4px 12px rgba(25, 42, 77, 0.06);
          padding: 0.52rem 0.7rem;
        }
        .chatbot-studio-page .studio-key-boolean.is-disabled {
          opacity: 0.62;
        }
        .chatbot-studio-page .studio-key-boolean-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.55rem;
          margin-bottom: 0.35rem;
        }
        .chatbot-studio-page .studio-key-boolean-presence .form-check-label {
          font-size: 0.72rem;
          color: #4f6286;
          font-weight: 600;
        }
        .chatbot-studio-page .studio-key-boolean-value .form-check-label {
          font-size: 0.78rem;
          color: #223b67;
          font-weight: 700;
          min-width: 2rem;
        }
        .chatbot-studio-page .studio-topic-item {
          border: 1px solid #d5e2f6;
          border-radius: 13px;
          padding: 0.78rem;
          margin-bottom: 0.7rem;
          background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
          box-shadow: 0 7px 15px rgba(16, 33, 62, 0.06);
        }
        .chatbot-studio-page .studio-topic-item .border-top {
          border-top: 1px dashed #c7d8f2 !important;
        }
        .chatbot-studio-page .studio-details summary {
          cursor: pointer;
          color: #1f3f82;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          border-radius: 999px;
          padding: 0.35rem 0.7rem;
          background: rgba(68, 113, 212, 0.08);
        }
        .chatbot-studio-page .studio-details summary:hover {
          color: #11234a;
          background: rgba(68, 113, 212, 0.15);
        }
        .chatbot-studio-page .studio-code-input,
        .chatbot-studio-page .studio-code-output {
          min-height: 70vh;
          font-family: Menlo, Consolas, monospace;
          font-size: 12px;
          border-radius: 13px;
          border: 1px solid #1f3050;
          background: linear-gradient(180deg, #0f1c34 0%, #111f3a 100%);
          color: #e0ecff;
          scrollbar-width: thin;
          scrollbar-color: #9cb8ee rgba(12, 24, 45, 0.32);
        }
        .chatbot-studio-page .studio-code-input::-webkit-scrollbar,
        .chatbot-studio-page .studio-code-output::-webkit-scrollbar {
          width: 8px;
        }
        .chatbot-studio-page .studio-code-input::-webkit-scrollbar-thumb,
        .chatbot-studio-page .studio-code-output::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #c2d4f6 0%, #9cb8ee 100%);
          border-radius: 999px;
        }
        .chatbot-studio-page .studio-code-input::-webkit-scrollbar-track,
        .chatbot-studio-page .studio-code-output::-webkit-scrollbar-track {
          background: rgba(12, 24, 45, 0.32);
          border-radius: 999px;
        }
        .chatbot-studio-page .studio-code-output {
          min-height: 28vh;
          border-color: #d4e0f4;
          background: linear-gradient(180deg, #f9fbff 0%, #f3f7ff 100%);
          color: #152952;
        }
        .chatbot-studio-page .studio-code-input:focus,
        .chatbot-studio-page .studio-code-output:focus {
          border-color: #4f7fd7;
          box-shadow: 0 0 0 0.2rem rgba(53, 99, 190, 0.18);
        }
        .chatbot-studio-page .studio-code-input.studio-code-input-error {
          border-color: #dc3545 !important;
          border-width: 3px !important;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.18) !important;
        }
        .chatbot-studio-page .studio-error-list li {
          margin-bottom: 0.22rem;
        }
        .chatbot-studio-page .form-control-color {
          border-radius: 12px !important;
          border: 1px solid #ceddf5;
          padding: 0.15rem !important;
          background: #fff;
        }
        @media (max-width: 1199.98px) {
          .chatbot-studio-page {
            border-radius: 16px;
          }
          .chatbot-studio-page .studio-scroll-body {
            max-height: none;
          }
          .chatbot-studio-page .studio-toolbar {
            position: static;
          }
          .chatbot-studio-page .studio-hero-meta {
            margin-top: 0.7rem;
          }
        }
        @media (max-width: 767.98px) {
          .chatbot-studio-page .studio-hero {
            padding: 1rem;
          }
          .chatbot-studio-page .studio-kicker {
            font-size: 0.68rem;
          }
          .chatbot-studio-page .studio-meta-pill {
            font-size: 0.7rem;
          }
        }
      `}</style>
      <div className="studio-glow studio-glow-1" />
      <div className="studio-glow studio-glow-2" />

      <div className="studio-shell">
        <div className="studio-hero mb-3">

          <h4 className="mb-1">Chatbot Config Studio</h4>
          <p className="mb-0">
            Simple Builder for non-technical users + Advanced JSON editor with
            two-way sync.
          </p>
          <div className="studio-hero-meta">
            <span className="studio-meta-pill">
              Mode: {mode === "simple" ? "Simple" : "Advanced"}
            </span>
            <span className="studio-meta-pill">Topics: {topics.length}</span>
            <span className="studio-meta-pill">
              Model: {configObj.model || "Not set"}
            </span>
          </div>
        </div>

        <div className={`alert alert-${status.type} studio-alert`} role="alert">
          {status.type === "danger" && statusErrorItems.length > 1 ? (
            <>
              <div className="fw-semibold mb-1">{status.text}</div>
              <ul className="mb-0 ps-3 studio-error-list">
                {statusErrorItems.map((item, idx) => (
                  <li key={`status-error-${idx}`}>{item}</li>
                ))}
              </ul>
            </>
          ) : (
            status.type === "danger" && statusErrorItems.length === 1
              ? statusErrorItems[0]
              : status.text
          )}
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3 studio-toolbar">
          <div className="studio-mode-group" role="group" aria-label="Mode switch">
            <button
              type="button"
              className={`btn ${mode === "simple" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setMode("simple")}
            >
              Simple Mode
            </button>
            <button
              type="button"
              className={`btn ${mode === "advanced" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setMode("advanced")}
            >
              Advanced Mode
            </button>
          </div>

          <button className="btn btn-primary btn-sm" onClick={handleValidate}>
            Validate
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleFormatJson}>
            Format JSON
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleImportClick}>
            Import JSON
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleCopyOutput}>
            Copy Output
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleExport}>
            Export Output
          </button>
          <button className="btn btn-outline-danger btn-sm" onClick={handleReset}>
            Reset Defaults
          </button>
          <button
            className="btn btn-sm ms-auto studio-run-btn"
            onClick={handleRunChatbot}
            disabled={isRunning}
          >
            {isRunning ? "Launching..." : "Run Chatbot"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="d-none"
            onChange={handleImportFile}
          />
          <input
            ref={transcriptFileRef}
            type="file"
            accept="text/plain,text/markdown,text/csv,application/json,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.txt,.md,.csv,.json,.pdf,.docx"
            className="d-none"
            onChange={handleTranscriptFileUpload}
          />
        </div>

        <div className="row g-3">
          <div className={simpleCol}>
            <div className="card h-100 studio-card">
              <div className="card-header fw-semibold studio-card-header">Simple Builder</div>
              <div className="card-body studio-scroll-body">
                <div className="studio-section">
                  <h6 className="mb-2">API</h6>
                  <div className="row g-2">
                    <div className="col-12">
                      <ConfigKeyField
                        configKey="Chatbot_API_URL"
                        label="Chatbot API URL"
                        enabled={isKeyEnabled("Chatbot_API_URL")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={configObj.Chatbot_API_URL || ""}
                          onChange={(e) => setField("Chatbot_API_URL", e.target.value)}
                          placeholder="https://.../chatbot-api"
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12">
                      <ConfigKeyField
                        configKey="recaptchaSiteKey"
                        label="reCAPTCHA Site Key"
                        enabled={isKeyEnabled("recaptchaSiteKey")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={configObj.recaptchaSiteKey || ""}
                          onChange={(e) => setField("recaptchaSiteKey", e.target.value)}
                        />
                      </ConfigKeyField>
                    </div>
                  </div>
                </div>

                <div className="studio-section">
                  <div className="studio-section-header">
                    <div className="d-flex align-items-center gap-2">
                      <input
                        className="form-check-input m-0"
                        type="checkbox"
                        id="studio-webinar-context-heading"
                        checked={webinarContextSectionEnabled}
                        onChange={(e) =>
                          setKeyGroupEnabled(
                            "__webinarContextSection",
                            WEBINAR_CONTEXT_SECTION_KEYS,
                            e.target.checked
                          )
                        }
                      />
                      <label className="mb-0 fw-semibold" htmlFor="studio-webinar-context-heading">
                        Webinar Context
                      </label>
                    </div>
                    <SummarizeButton disabled={!webinarContextSectionEnabled} />
                  </div>
                  <fieldset
                    className={`studio-subsection-fieldset ${webinarContextSectionEnabled ? "" : "is-disabled"
                      }`}
                    disabled={!webinarContextSectionEnabled}
                  >
                    <div className="row g-2">
                      <div className="col-12 col-md-6">
                        <ConfigKeyField
                          configKey="display_text"
                          label="Display Text"
                          enabled={isKeyEnabled("display_text")}
                          onToggle={toggleKeyEnabled}
                        >
                          <input
                            className="form-control form-control-sm"
                            value={configObj.display_text || ""}
                            onChange={(e) => setField("display_text", e.target.value)}
                            placeholder="Optional quick-action display text override"
                          />
                        </ConfigKeyField>
                      </div>
                      <div className="col-12 col-md-6">
                        <ConfigKeyField
                          configKey="prompt"
                          label="Prompt"
                          enabled={isKeyEnabled("prompt")}
                          onToggle={toggleKeyEnabled}
                        >
                          <input
                            className="form-control form-control-sm"
                            value={configObj.prompt || ""}
                            onChange={(e) => setField("prompt", e.target.value)}
                            placeholder="Optional quick-action prompt override"
                          />
                        </ConfigKeyField>
                      </div>
                      <div className="col-12">
                        <ConfigKeyField
                          configKey="webinarSummary"
                          label="Webinar Summary"
                          enabled={isKeyEnabled("webinarSummary")}
                          onToggle={toggleKeyEnabled}
                        >
                          <textarea
                            className="form-control form-control-sm"
                            rows={4}
                            value={configObj.webinarSummary || ""}
                            onChange={(e) => setField("webinarSummary", e.target.value)}
                            placeholder="This summary will be injected instantly when 'Summarize Webinar' is clicked."
                          />
                        </ConfigKeyField>
                      </div>
                      <div className="col-12">
                        <ConfigKeyField
                          configKey="webinarTranscript"
                          label="Webinar Transcript"
                          enabled={isKeyEnabled("webinarTranscript")}
                          onToggle={toggleKeyEnabled}
                        >
                          <textarea
                            className="form-control form-control-sm"
                            rows={6}
                            value={configObj.webinarTranscript || ""}
                            onChange={(e) => setField("webinarTranscript", e.target.value)}
                            placeholder="Paste full transcript here or upload .txt/.md/.csv/.json"
                          />
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <div className="d-flex flex-column">
                            {/* <small className="text-muted">
                              Used only on the first user question after page load.
                            </small> */}
                            <small className="text-muted">
                              Supported transcript formats: .txt, .md, .csv, .json
                            </small>
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={handleTranscriptUploadClick}
                          >
                              Upload Transcript
                            </button>
                          </div>
                        </ConfigKeyField>
                      </div>
                    </div>
                  </fieldset>
                </div>

                <div className="studio-section">
                  <h6 className="mb-2">Branding</h6>
                  <div className="row g-2">
                    <div className="col-12 col-md-6">
                      <ConfigKeyField
                        configKey="title"
                        label="Chatbot Name (Title)"
                        enabled={isKeyEnabled("title")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={configObj.title || ""}
                          onChange={(e) => setField("title", e.target.value)}
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12 col-md-6">
                      <ConfigKeyField
                        configKey="customerName"
                        label="Customer Name"
                        enabled={isKeyEnabled("customerName")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={configObj.customerName || ""}
                          onChange={(e) => setField("customerName", e.target.value)}
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12">
                      <ConfigKeyField
                        configKey="customerLogo"
                        label="Customer Logo URL / Path"
                        enabled={isKeyEnabled("customerLogo")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={configObj.customerLogo || ""}
                          onChange={(e) => setField("customerLogo", e.target.value)}
                          placeholder="https://... or /customer-logo.svg"
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12">
                      <ConfigKeyField
                        configKey="greeting"
                        label="Greeting"
                        enabled={isKeyEnabled("greeting")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={configObj.greeting || ""}
                          onChange={(e) => setField("greeting", e.target.value)}
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12">
                      <ConfigKeyField
                        configKey="inputPlaceholder"
                        label="Input Placeholder"
                        enabled={isKeyEnabled("inputPlaceholder")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={configObj.inputPlaceholder || ""}
                          onChange={(e) => setField("inputPlaceholder", e.target.value)}
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12 col-md-6">
                      <ColorField
                        configKey="primaryColor"
                        label="Primary Color"
                        enabled={isKeyEnabled("primaryColor")}
                        onToggle={toggleKeyEnabled}
                        value={configObj.primaryColor || ""}
                        onChange={(value) => setField("primaryColor", value)}
                        fallback="#2563eb"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <ConfigKeyField
                        configKey="buttonBackground"
                        label="Button Background"
                        enabled={isKeyEnabled("buttonBackground")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={configObj.buttonBackground || ""}
                          onChange={(e) => setField("buttonBackground", e.target.value)}
                        />
                      </ConfigKeyField>
                    </div>
                  </div>
                </div>

                <div className="studio-section">
                  <h6 className="mb-2">Behavior</h6>
                  <div className="row g-2">
                    <div className="col-12 col-md-6">
                      <ConfigKeyField
                        configKey="model"
                        label="Model"
                        enabled={isKeyEnabled("model")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={configObj.model || ""}
                          onChange={(e) => setField("model", e.target.value)}
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12 col-md-6">
                      <ConfigKeyField
                        configKey="timeoutMs"
                        label="Timeout (ms)"
                        enabled={isKeyEnabled("timeoutMs")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={configObj.timeoutMs ?? ""}
                          onChange={(e) => setNumberField("timeoutMs", e.target.value)}
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12 col-md-6">
                      <ConfigKeyField
                        configKey="triggerBadgeText"
                        label="Trigger Badge Text"
                        enabled={isKeyEnabled("triggerBadgeText")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={configObj.triggerBadgeText || ""}
                          onChange={(e) => setField("triggerBadgeText", e.target.value)}
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12 col-md-6">
                      <ConfigKeyField
                        configKey="triggerLabel"
                        label="Trigger Label"
                        enabled={isKeyEnabled("triggerLabel")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={configObj.triggerLabel || ""}
                          onChange={(e) => setField("triggerLabel", e.target.value)}
                          placeholder="Falls back to chatbot title"
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12 col-md-6">
                      <ConfigKeyField
                        configKey="triggerPosition"
                        label="Trigger Position"
                        enabled={isKeyEnabled("triggerPosition")}
                        onToggle={toggleKeyEnabled}
                      >
                        <select
                          className="form-select form-select-sm"
                          value={configObj.triggerPosition || "bottom-right"}
                          onChange={(e) => setField("triggerPosition", e.target.value)}
                        >
                          <option value="bottom-right">bottom-right</option>
                          <option value="bottom-left">bottom-left</option>
                        </select>
                      </ConfigKeyField>
                    </div>
                    <div className="col-12 col-md-6">
                      <ConfigKeyField
                        configKey="triggerIcon"
                        label="Trigger Icon URL / Path"
                        enabled={isKeyEnabled("triggerIcon")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={configObj.triggerIcon || ""}
                          onChange={(e) => setField("triggerIcon", e.target.value)}
                          placeholder="https://... or /icons/trigger.svg"
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12 col-md-6">
                      <ConfigKeyField
                        configKey="triggerIconSize"
                        label="Trigger Icon Size"
                        enabled={isKeyEnabled("triggerIconSize")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={configObj.triggerIconSize ?? 44}
                          min={30}
                          max={72}
                          onChange={(e) => setNumberField("triggerIconSize", e.target.value)}
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12 col-md-6">
                      <ConfigKeyField
                        configKey="triggerIconRadius"
                        label="Trigger Icon Radius"
                        enabled={isKeyEnabled("triggerIconRadius")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={configObj.triggerIconRadius ?? 14}
                          min={0}
                          max={40}
                          onChange={(e) => setNumberField("triggerIconRadius", e.target.value)}
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12 col-md-6">
                      <ColorField
                        configKey="triggerIconBackground"
                        label="Trigger Icon Background"
                        enabled={isKeyEnabled("triggerIconBackground")}
                        onToggle={toggleKeyEnabled}
                        value={configObj.triggerIconBackground || ""}
                        onChange={(value) => setField("triggerIconBackground", value)}
                        fallback="#2563eb"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <ColorField
                        configKey="triggerIconColor"
                        label="Trigger Icon Color"
                        enabled={isKeyEnabled("triggerIconColor")}
                        onToggle={toggleKeyEnabled}
                        value={configObj.triggerIconColor || ""}
                        onChange={(value) => setField("triggerIconColor", value)}
                        fallback="#ffffff"
                      />
                    </div>
                  </div>

                  <div className="mt-3 studio-toggles">
                    <ConfigKeyBooleanField
                      configKey="debug"
                      label="Debug"
                      enabled={isKeyEnabled("debug")}
                      checked={asBoolean(configObj.debug)}
                      onToggle={toggleKeyEnabled}
                      onChange={(value) => setField("debug", value)}
                      id="studio-debug"
                    />
                    <ConfigKeyBooleanField
                      configKey="newBadge"
                      label="New Badge"
                      enabled={isKeyEnabled("newBadge")}
                      checked={asBoolean(configObj.newBadge)}
                      onToggle={toggleKeyEnabled}
                      onChange={(value) => setField("newBadge", value)}
                      id="studio-new-badge"
                    />
                    <ConfigKeyBooleanField
                      configKey="showTriggerPill"
                      label="Show Trigger Pill"
                      enabled={isKeyEnabled("showTriggerPill")}
                      checked={asBoolean(configObj.showTriggerPill)}
                      onToggle={toggleKeyEnabled}
                      onChange={(value) => setField("showTriggerPill", value)}
                      id="studio-trigger-pill"
                    />
                    <ConfigKeyBooleanField
                      configKey="headerVisible"
                      label="Header Visible"
                      enabled={isKeyEnabled("headerVisible")}
                      checked={asBoolean(configObj.headerVisible)}
                      onToggle={toggleKeyEnabled}
                      onChange={(value) => setField("headerVisible", value)}
                      id="studio-header-visible"
                    />
                  </div>
                </div>

                <div className="studio-section">
                  <div className="col-12 mt-2">
                    <h6 className="mb-1">Send Button</h6>
                  </div>
                  <div className="row g-2">
                    <div className="col-12 col-md-6">
                      <ColorField
                        configKey="sendButtonBackground"
                        label="Send Button Background"
                        enabled={isKeyEnabled("sendButtonBackground")}
                        onToggle={toggleKeyEnabled}
                        value={configObj.sendButtonBackground || ""}
                        onChange={(value) => setField("sendButtonBackground", value)}
                        fallback="#2563eb"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <ColorField
                        configKey="sendButtonHoverBackground"
                        label="Send Button Hover"
                        enabled={isKeyEnabled("sendButtonHoverBackground")}
                        onToggle={toggleKeyEnabled}
                        value={configObj.sendButtonHoverBackground || ""}
                        onChange={(value) => setField("sendButtonHoverBackground", value)}
                        fallback="#1d4ed8"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <ColorField
                        configKey="sendButtonIconColor"
                        label="Send Button Icon Color"
                        enabled={isKeyEnabled("sendButtonIconColor")}
                        onToggle={toggleKeyEnabled}
                        value={configObj.sendButtonIconColor || ""}
                        onChange={(value) => setField("sendButtonIconColor", value)}
                        fallback="#ffffff"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <ConfigKeyField
                        configKey="sendButtonRadius"
                        label="Send Button Radius"
                        enabled={isKeyEnabled("sendButtonRadius")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={configObj.sendButtonRadius ?? 18}
                          min={0}
                          max={24}
                          onChange={(e) => setNumberField("sendButtonRadius", e.target.value)}
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12">
                      <ConfigKeyField
                        configKey="sendButtonLogo"
                        label="Send Button Logo URL (Optional)"
                        enabled={isKeyEnabled("sendButtonLogo")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={configObj.sendButtonLogo || ""}
                          onChange={(e) => setField("sendButtonLogo", e.target.value)}
                          placeholder="https://.../logo.svg"
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12 mt-3">
                      <div className="d-flex align-items-center gap-2">
                        <input
                          className="form-check-input m-0"
                          type="checkbox"
                          id="studio-attach-heading"
                          checked={attachmentSectionEnabled}
                          onChange={(e) =>
                            setSectionEnabled(
                              "showAttachmentBtn",
                              ATTACHMENT_SECTION_KEYS,
                              e.target.checked
                            )
                          }
                        />
                        <label className="mb-0 fw-semibold" htmlFor="studio-attach-heading">
                          Attachment Button
                        </label>
                      </div>
                    </div>
                    <div className="col-12">
                      <fieldset
                        className={`studio-subsection-fieldset ${attachmentSectionEnabled ? "" : "is-disabled"
                          }`}
                        disabled={!attachmentSectionEnabled}
                      >
                        <div className="row g-2">
                          <div className="col-12 col-md-6">
                            <ColorField
                              configKey="attachmentButtonBackground"
                              label="Attachment Button Background"
                              enabled={isKeyEnabled("attachmentButtonBackground")}
                              onToggle={toggleKeyEnabled}
                              value={configObj.attachmentButtonBackground || ""}
                              onChange={(value) => setField("attachmentButtonBackground", value)}
                              fallback="#ffffff"
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <ColorField
                              configKey="attachmentButtonHoverBackground"
                              label="Attachment Button Hover"
                              enabled={isKeyEnabled("attachmentButtonHoverBackground")}
                              onToggle={toggleKeyEnabled}
                              value={configObj.attachmentButtonHoverBackground || ""}
                              onChange={(value) => setField("attachmentButtonHoverBackground", value)}
                              fallback="#f3f4f6"
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <ColorField
                              configKey="attachmentButtonIconColor"
                              label="Attachment Icon Color"
                              enabled={isKeyEnabled("attachmentButtonIconColor")}
                              onToggle={toggleKeyEnabled}
                              value={configObj.attachmentButtonIconColor || ""}
                              onChange={(value) => setField("attachmentButtonIconColor", value)}
                              fallback="#9ca3af"
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <ConfigKeyField
                              configKey="attachmentButtonRadius"
                              label="Attachment Button Radius"
                              enabled={isKeyEnabled("attachmentButtonRadius")}
                              onToggle={toggleKeyEnabled}
                            >
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={configObj.attachmentButtonRadius ?? 8}
                                min={0}
                                max={24}
                                onChange={(e) => setNumberField("attachmentButtonRadius", e.target.value)}
                              />
                            </ConfigKeyField>
                          </div>
                          <div className="col-12">
                            <ConfigKeyField
                              configKey="attachmentButtonLogo"
                              label="Attachment Button Logo URL (Optional)"
                              enabled={isKeyEnabled("attachmentButtonLogo")}
                              onToggle={toggleKeyEnabled}
                            >
                              <input
                                className="form-control form-control-sm"
                                value={configObj.attachmentButtonLogo || ""}
                                onChange={(e) => setField("attachmentButtonLogo", e.target.value)}
                                placeholder="https://.../attach.svg"
                              />
                            </ConfigKeyField>
                          </div>
                        </div>
                      </fieldset>
                    </div>
                    <div className="col-12 mt-3">
                      <div className="d-flex align-items-center gap-2">
                        <input
                          className="form-check-input m-0"
                          type="checkbox"
                          id="studio-mic-heading"
                          checked={micSectionEnabled}
                          onChange={(e) =>
                            setSectionEnabled(
                              "showMicBtn",
                              MIC_SECTION_KEYS,
                              e.target.checked
                            )
                          }
                        />
                        <label className="mb-0 fw-semibold" htmlFor="studio-mic-heading">
                          Mic Button
                        </label>
                      </div>
                    </div>
                    <div className="col-12">
                      <fieldset
                        className={`studio-subsection-fieldset ${micSectionEnabled ? "" : "is-disabled"
                          }`}
                        disabled={!micSectionEnabled}
                      >
                        <div className="row g-2">
                          <div className="col-12 col-md-6">
                            <ColorField
                              configKey="micButtonBackground"
                              label="Mic Button Background"
                              enabled={isKeyEnabled("micButtonBackground")}
                              onToggle={toggleKeyEnabled}
                              value={configObj.micButtonBackground || ""}
                              onChange={(value) => setField("micButtonBackground", value)}
                              fallback="#ffffff"
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <ColorField
                              configKey="micButtonHoverBackground"
                              label="Mic Button Hover"
                              enabled={isKeyEnabled("micButtonHoverBackground")}
                              onToggle={toggleKeyEnabled}
                              value={configObj.micButtonHoverBackground || ""}
                              onChange={(value) => setField("micButtonHoverBackground", value)}
                              fallback="#f3f4f6"
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <ColorField
                              configKey="micButtonIconColor"
                              label="Mic Icon Color"
                              enabled={isKeyEnabled("micButtonIconColor")}
                              onToggle={toggleKeyEnabled}
                              value={configObj.micButtonIconColor || ""}
                              onChange={(value) => setField("micButtonIconColor", value)}
                              fallback="#9ca3af"
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <ConfigKeyField
                              configKey="micButtonRadius"
                              label="Mic Button Radius"
                              enabled={isKeyEnabled("micButtonRadius")}
                              onToggle={toggleKeyEnabled}
                            >
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={configObj.micButtonRadius ?? 8}
                                min={0}
                                max={24}
                                onChange={(e) => setNumberField("micButtonRadius", e.target.value)}
                              />
                            </ConfigKeyField>
                          </div>
                          <div className="col-12">
                            <ConfigKeyField
                              configKey="micButtonLogo"
                              label="Mic Button Logo URL (Optional)"
                              enabled={isKeyEnabled("micButtonLogo")}
                              onToggle={toggleKeyEnabled}
                            >
                              <input
                                className="form-control form-control-sm"
                                value={configObj.micButtonLogo || ""}
                                onChange={(e) => setField("micButtonLogo", e.target.value)}
                                placeholder="https://.../mic.svg"
                              />
                            </ConfigKeyField>
                          </div>
                        </div>
                      </fieldset>
                    </div>
                    <div className="col-12 mt-3">
                      <div className="d-flex align-items-center gap-2">
                        <input
                          className="form-check-input m-0"
                          type="checkbox"
                          id="studio-expand-heading"
                          checked={expandSectionEnabled}
                          onChange={(e) =>
                            setSectionEnabled(
                              "expandButtonVisible",
                              EXPAND_SECTION_KEYS,
                              e.target.checked
                            )
                          }
                        />
                        <label className="mb-0 fw-semibold" htmlFor="studio-expand-heading">
                          Expand Button
                        </label>
                      </div>
                    </div>
                    <div className="col-12">
                      <fieldset
                        className={`studio-subsection-fieldset ${expandSectionEnabled ? "" : "is-disabled"
                          }`}
                        disabled={!expandSectionEnabled}
                      >
                        <div className="row g-2">
                          <div className="col-12 col-md-6">
                            <ColorField
                              configKey="expandButtonBackground"
                              label="Expand Button Background"
                              enabled={isKeyEnabled("expandButtonBackground")}
                              onToggle={toggleKeyEnabled}
                              value={configObj.expandButtonBackground || ""}
                              onChange={(value) => setField("expandButtonBackground", value)}
                              fallback="#ffffff"
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <ColorField
                              configKey="expandButtonHoverBackground"
                              label="Expand Button Hover"
                              enabled={isKeyEnabled("expandButtonHoverBackground")}
                              onToggle={toggleKeyEnabled}
                              value={configObj.expandButtonHoverBackground || ""}
                              onChange={(value) => setField("expandButtonHoverBackground", value)}
                              fallback="#f3f4f6"
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <ColorField
                              configKey="expandButtonIconColor"
                              label="Expand Icon Color"
                              enabled={isKeyEnabled("expandButtonIconColor")}
                              onToggle={toggleKeyEnabled}
                              value={configObj.expandButtonIconColor || ""}
                              onChange={(value) => setField("expandButtonIconColor", value)}
                              fallback="#6b7280"
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <ConfigKeyField
                              configKey="expandButtonRadius"
                              label="Expand Button Radius"
                              enabled={isKeyEnabled("expandButtonRadius")}
                              onToggle={toggleKeyEnabled}
                            >
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={configObj.expandButtonRadius ?? 10}
                                min={0}
                                max={24}
                                onChange={(e) => setNumberField("expandButtonRadius", e.target.value)}
                              />
                            </ConfigKeyField>
                          </div>
                          <div className="col-12">
                            <ConfigKeyField
                              configKey="expandButtonLogo"
                              label="Expand Button Logo URL (Optional)"
                              enabled={isKeyEnabled("expandButtonLogo")}
                              onToggle={toggleKeyEnabled}
                            >
                              <input
                                className="form-control form-control-sm"
                                value={configObj.expandButtonLogo || ""}
                                onChange={(e) => setField("expandButtonLogo", e.target.value)}
                                placeholder="https://.../expand.svg"
                              />
                            </ConfigKeyField>
                          </div>
                        </div>
                      </fieldset>
                    </div>
                    <div className="col-12 mt-3">
                      <h6 className="mb-1">Close Button</h6>
                    </div>
                    <div className="col-12 col-md-6">
                      <ColorField
                        configKey="closeButtonBackground"
                        label="Close Button Background"
                        enabled={isKeyEnabled("closeButtonBackground")}
                        onToggle={toggleKeyEnabled}
                        value={configObj.closeButtonBackground || ""}
                        onChange={(value) => setField("closeButtonBackground", value)}
                        fallback="#ffffff"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <ColorField
                        configKey="closeButtonHoverBackground"
                        label="Close Button Hover"
                        enabled={isKeyEnabled("closeButtonHoverBackground")}
                        onToggle={toggleKeyEnabled}
                        value={configObj.closeButtonHoverBackground || ""}
                        onChange={(value) => setField("closeButtonHoverBackground", value)}
                        fallback="#f3f4f6"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <ColorField
                        configKey="closeButtonIconColor"
                        label="Close Icon Color"
                        enabled={isKeyEnabled("closeButtonIconColor")}
                        onToggle={toggleKeyEnabled}
                        value={configObj.closeButtonIconColor || ""}
                        onChange={(value) => setField("closeButtonIconColor", value)}
                        fallback="#6b7280"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <ConfigKeyField
                        configKey="closeButtonRadius"
                        label="Close Button Radius"
                        enabled={isKeyEnabled("closeButtonRadius")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={configObj.closeButtonRadius ?? 10}
                          min={0}
                          max={24}
                          onChange={(e) => setNumberField("closeButtonRadius", e.target.value)}
                        />
                      </ConfigKeyField>
                    </div>
                    <div className="col-12">
                      <ConfigKeyField
                        configKey="closeButtonLogo"
                        label="Close Button Logo URL (Optional)"
                        enabled={isKeyEnabled("closeButtonLogo")}
                        onToggle={toggleKeyEnabled}
                      >
                        <input
                          className="form-control form-control-sm"
                          value={configObj.closeButtonLogo || ""}
                          onChange={(e) => setField("closeButtonLogo", e.target.value)}
                          placeholder="https://.../close.svg"
                        />
                      </ConfigKeyField>
                    </div>
                  </div>
                </div>

                <div className="studio-section">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <input
                      className="form-check-input m-0"
                      type="checkbox"
                      id="studio-bubble-heading"
                      checked={bubbleSectionEnabled}
                      onChange={(e) =>
                        setKeyGroupEnabled(
                          "__bubbleSection",
                          BUBBLE_SECTION_KEYS,
                          e.target.checked
                        )
                      }
                    />
                    <label className="mb-0 fw-semibold" htmlFor="studio-bubble-heading">
                      Bubble Colors
                    </label>
                  </div>
                  <fieldset
                    className={`studio-subsection-fieldset ${bubbleSectionEnabled ? "" : "is-disabled"
                      }`}
                    disabled={!bubbleSectionEnabled}
                  >
                    <div className="row g-2">
                      <div className="col-12 col-md-6">
                        <ColorField
                          configKey="userBubbleColor"
                          label="User Bubble Color"
                          enabled={isKeyEnabled("userBubbleColor")}
                          onToggle={toggleKeyEnabled}
                          value={configObj.userBubbleColor || ""}
                          onChange={(value) => setField("userBubbleColor", value)}
                          fallback="#2563eb"
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <ColorField
                          configKey="userBubbleTextColor"
                          label="User Bubble Text"
                          enabled={isKeyEnabled("userBubbleTextColor")}
                          onToggle={toggleKeyEnabled}
                          value={configObj.userBubbleTextColor || ""}
                          onChange={(value) => setField("userBubbleTextColor", value)}
                          fallback="#ffffff"
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <ColorField
                          configKey="botBubbleColor"
                          label="Bot Bubble Color"
                          enabled={isKeyEnabled("botBubbleColor")}
                          onToggle={toggleKeyEnabled}
                          value={configObj.botBubbleColor || ""}
                          onChange={(value) => setField("botBubbleColor", value)}
                          fallback="#f3f4f6"
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <ColorField
                          configKey="botBubbleTextColor"
                          label="Bot Bubble Text Color"
                          enabled={isKeyEnabled("botBubbleTextColor")}
                          onToggle={toggleKeyEnabled}
                          value={configObj.botBubbleTextColor || ""}
                          onChange={(value) => setField("botBubbleTextColor", value)}
                          fallback="#111827"
                        />
                      </div>
                    </div>
                  </fieldset>
                </div>

                <div className="studio-section">
                  <ConfigKeyField
                    configKey="topics"
                    label="Topics"
                    enabled={isKeyEnabled("topics")}
                    onToggle={toggleKeyEnabled}
                    className="form-label form-label-sm fw-semibold"
                  >
                    <div className="d-flex justify-content-end align-items-center mb-2">
                      <button className="btn btn-sm btn-outline-primary" onClick={addTopic}>
                        Add Topic
                      </button>
                    </div>

                    {topics.length === 0 && (
                      <p className="text-muted mb-0">No topics configured yet.</p>
                    )}

                    {topics.map((topic, idx) => {
                      const dropdownOptions = Array.isArray(topic?.dropdown?.options)
                        ? topic.dropdown.options
                        : [];

                      return (
                        <div key={`topic-${idx}`} className="studio-topic-item">
                          <div className="row g-2">
                            <div className="col-12 col-md-6">
                              <label className="form-label form-label-sm">Label</label>
                              <input
                                className="form-control form-control-sm"
                                value={topic.label || ""}
                                onChange={(e) => updateTopic(idx, "label", e.target.value)}
                              />
                            </div>
                            <div className="col-12 col-md-4">
                              <label className="form-label form-label-sm">Icon URL / Path</label>
                              <input
                                className="form-control form-control-sm"
                                value={topic.icon || ""}
                                onChange={(e) => updateTopic(idx, "icon", e.target.value)}
                                placeholder="https://... or /icons/topic.svg"
                              />
                            </div>
                            <div className="col-12 col-md-2 d-flex align-items-end">
                              <button
                                className="btn btn-sm btn-outline-danger w-100"
                                onClick={() => removeTopic(idx)}
                              >
                                Remove
                              </button>
                            </div>
                            {!topic.dropdown && (
                              <div className="col-12">
                                <label className="form-label form-label-sm">Prompt</label>
                                <input
                                  className="form-control form-control-sm"
                                  value={topic.prompt || ""}
                                  onChange={(e) => updateTopic(idx, "prompt", e.target.value)}
                                />
                              </div>
                            )}
                          </div>

                          <div className="form-check mt-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`topic-dropdown-${idx}`}
                              checked={!!topic.dropdown}
                              onChange={(e) => toggleTopicDropdown(idx, e.target.checked)}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`topic-dropdown-${idx}`}
                            >
                              Enable Dropdown
                            </label>
                          </div>

                          {topic.dropdown && (
                            <div className="mt-2 border-top pt-2">
                              <div className="row g-2">
                                <div className="col-12 col-md-6">
                                  <label className="form-label form-label-sm">Dropdown Title</label>
                                  <input
                                    className="form-control form-control-sm"
                                    value={topic.dropdown.title || ""}
                                    onChange={(e) =>
                                      updateTopicDropdown(idx, "title", e.target.value)
                                    }
                                  />
                                </div>
                                <div className="col-12 col-md-6">
                                  <label className="form-label form-label-sm">Placeholder</label>
                                  <input
                                    className="form-control form-control-sm"
                                    value={topic.dropdown.placeholder || ""}
                                    onChange={(e) =>
                                      updateTopicDropdown(idx, "placeholder", e.target.value)
                                    }
                                  />
                                </div>
                                <div className="col-12">
                                  <label className="form-label form-label-sm">Description</label>
                                  <input
                                    className="form-control form-control-sm"
                                    value={topic.dropdown.description || ""}
                                    onChange={(e) =>
                                      updateTopicDropdown(idx, "description", e.target.value)
                                    }
                                  />
                                </div>
                                <div className="col-12 col-md-6">
                                  <label className="form-label form-label-sm">Submit Label</label>
                                  <input
                                    className="form-control form-control-sm"
                                    value={topic.dropdown.submitLabel || ""}
                                    onChange={(e) =>
                                      updateTopicDropdown(idx, "submitLabel", e.target.value)
                                    }
                                  />
                                </div>
                              </div>

                              <div className="d-flex justify-content-between align-items-center mt-2 mb-1">
                                <label className="form-label form-label-sm mb-0">Dropdown Options</label>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => addDropdownOption(idx)}
                                >
                                  Add Option
                                </button>
                              </div>

                              {dropdownOptions.map((option, optionIdx) => (
                                <div
                                  key={`topic-${idx}-opt-${optionIdx}`}
                                  className="border rounded p-2 mb-2"
                                >
                                  <div className="row g-2">
                                    <div className="col-12 col-md-5">
                                      <label className="form-label form-label-sm">Option Label</label>
                                      <input
                                        className="form-control form-control-sm"
                                        value={getDropdownOptionLabel(option)}
                                        onChange={(e) =>
                                          updateDropdownOption(
                                            idx,
                                            optionIdx,
                                            "label",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>
                                    <div className="col-12 col-md-7">
                                      <label className="form-label form-label-sm">
                                        Template for This Option *
                                      </label>
                                      <textarea
                                        className="form-control form-control-sm"
                                        rows={2}
                                        value={getDropdownOptionTemplate(option)}
                                        onChange={(e) =>
                                          updateDropdownOption(
                                            idx,
                                            optionIdx,
                                            "template",
                                            e.target.value
                                          )
                                        }
                                        spellCheck={false}
                                        required
                                      />
                                    </div>
                                  </div>
                                  <div className="d-flex justify-content-end mt-2">
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeDropdownOption(idx, optionIdx)}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </ConfigKeyField>
                </div>

              </div>
            </div>
          </div>

          <div className={advancedCol}>
            <div className="card h-100 studio-card">
              <div className="card-header fw-semibold studio-card-header">
                <div className="studio-code-header">
                  <span>Advanced JSON (Editable)</span>
                  <button
                    type="button"
                    className="studio-code-copy-btn"
                    onClick={handleCopyAdvancedJson}
                  >
                    Copy JSON
                  </button>
                </div>
              </div>
              <div className="card-body">
                <textarea
                  className={`form-control studio-code-input ${advancedHasErrors ? "studio-code-input-error" : ""
                    }`}
                  value={configInput}
                  onChange={(e) => handleJsonInputChange(e.target.value)}
                  spellCheck={false}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col-12">
            <div className="card studio-card">
              <div className="card-header fw-semibold studio-card-header">
                Applied Config Output (Last Run)
              </div>
              <div className="card-body">
                <textarea
                  className="form-control studio-code-output"
                  value={appliedConfigOutput}
                  readOnly
                  spellCheck={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
